import type { Point } from "../scene/model";

export type LineStyle = "solid" | "dashed" | "dotted" | "hand";
export const lineStyles: LineStyle[] = ["solid", "dashed", "dotted", "hand"];
export function dashPattern(style: LineStyle, width: number): number[] {
  const w = Math.max(1, width);
  return style === "dotted"
    ? [0.01, w * 2.2]
    : style === "dashed"
      ? [w * 3.2, w * 2.1]
      : [];
}
export interface StrokeRun {
  points: Point[];
  closed: boolean;
}
/** Split by accumulated contour length; gaps never restart at vertices. */
export function dashRuns(points: Point[], pattern: number[]): StrokeRun[] {
  if (!pattern.length) return [{ points, closed: true }];
  const runs: StrokeRun[] = [];
  let index = 0,
    remaining = pattern[0]!,
    active: Point[] | null = null;
  for (let i = 0; i < points.length; i++) {
    const a = points[i]!,
      b = points[(i + 1) % points.length]!;
    const length = Math.hypot(b.x - a.x, b.y - a.y);
    let offset = 0;
    const at = (t: number) => ({
      x: a.x + ((b.x - a.x) * t) / length,
      y: a.y + ((b.y - a.y) * t) / length,
    });
    while (offset < length - 1e-10) {
      const take = Math.min(remaining, length - offset);
      if (index % 2 === 0) {
        if (!active) {
          active = [at(offset)];
          runs.push({ points: active, closed: false });
        }
        active.push(at(offset + take));
      }
      offset += take;
      remaining -= take;
      if (remaining < 1e-10) {
        index = (index + 1) % pattern.length;
        remaining = pattern[index]!;
        active = null;
      }
    }
  }
  const first = runs[0],
    last = runs.at(-1),
    origin = points[0];
  if (first && last && origin) {
    const end = last.points.at(-1)!;
    if (Math.hypot(end.x - origin.x, end.y - origin.y) < 1e-8) {
      if (first === last) {
        first.points.pop();
        first.closed = true;
      } else {
        first.points = [...last.points.slice(0, -1), ...first.points];
        runs.pop();
      }
    }
  }
  return runs;
}
/** A round join adds only the outside wedge, never a full disk beyond nearby butt caps. */
export function joinArc(before: Point, p: Point, after: Point) {
  const ax = p.x - before.x,
    ay = p.y - before.y,
    bx = after.x - p.x,
    by = after.y - p.y;
  const sweep = Math.atan2(ax * by - ay * bx, ax * bx + ay * by);
  return {
    start: sweep > 0 ? Math.atan2(-ax, ay) : Math.atan2(ax, -ay),
    sweep,
  };
}
export function runContains(
  run: StrokeRun,
  p: Point,
  radius: number,
  roundCaps: boolean,
) {
  const points = run.points,
    count = points.length;
  for (let i = 0; i < (run.closed ? count : count - 1); i++) {
    const a = points[i]!,
      b = points[(i + 1) % count]!,
      dx = b.x - a.x,
      dy = b.y - a.y;
    const length = Math.hypot(dx, dy),
      projection = ((p.x - a.x) * dx + (p.y - a.y) * dy) / length;
    if (
      length &&
      projection >= 0 &&
      projection <= length &&
      Math.abs((p.x - a.x) * dy - (p.y - a.y) * dx) / length <= radius
    )
      return true;
  }
  return points.some((a, i) => {
    if (Math.hypot(p.x - a.x, p.y - a.y) > radius) return false;
    if (!run.closed && (i === 0 || i === count - 1)) return roundCaps;
    const arc = joinArc(
      points[(i + count - 1) % count]!,
      a,
      points[(i + 1) % count]!,
    );
    if (Math.abs(arc.sweep) < 1e-12) return false;
    const delta =
      (Math.atan2(p.y - a.y, p.x - a.x) - arc.start) * Math.sign(arc.sweep);
    const angle = ((delta % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
    return angle <= Math.abs(arc.sweep) + 1e-10;
  });
}
